import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/http";
import { useAuth } from "@/auth/AuthContext";
import { useBranding } from "@/branding/BrandingLoader";
import { Button, Card, CardBody, CardHeader, FormError, Input, Label } from "@/components/ui";

interface DesktopDownload {
  os: "macos" | "windows" | "linux";
  label: string;
  href: string;
  manifestHref: string;
}

interface DownloadManifest {
  artifact_name?: string;
  artifact_size?: number;
  artifact_sha256?: string;
}

const DESKTOP_DOWNLOADS: DesktopDownload[] = [
  { os: "macos", label: "Download for macOS (v2)", href: "/downloads/native/macos", manifestHref: "/downloads/native/macos/manifest" },
  { os: "windows", label: "Download for Windows", href: "/downloads/native/windows", manifestHref: "/downloads/native/windows/manifest" },
  { os: "linux", label: "Download for Linux", href: "/downloads/native/linux", manifestHref: "/downloads/native/linux/manifest" },
];

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_048_576).toFixed(0)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function DownloadButton({ d }: { d: DesktopDownload }) {
  const [manifest, setManifest] = useState<DownloadManifest | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);

  // fetch manifest once on mount to learn whether artifact exists and its size
  const manifestHref = d.manifestHref;
  useEffect(() => {
    let cancelled = false;
    fetch(manifestHref)
      .then((r) => {
        if (!r.ok) { if (!cancelled) setAvailable(false); return null; }
        return r.json();
      })
      .then((data) => {
        if (!cancelled && data) { setManifest(data); setAvailable(true); }
      })
      .catch(() => { if (!cancelled) setAvailable(false); });
    return () => { cancelled = true; };
  }, [manifestHref]);

  const sizeLabel = manifest?.artifact_size ? formatBytes(manifest.artifact_size) : null;

  return (
    <a
      key={d.os}
      href={available === false ? undefined : d.href}
      aria-disabled={available === false}
      className={[
        "flex items-center justify-between rounded border px-3 py-1.5 text-xs",
        available === false
          ? "cursor-not-allowed border-slate-200 text-slate-400 dark:border-slate-800 dark:text-slate-600"
          : "border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800",
      ].join(" ")}
    >
      <span>{d.label}</span>
      <span className="text-slate-400 dark:text-slate-500 ml-2 shrink-0">
        {available === null ? "…" : available === false ? "Soon" : sizeLabel ?? "↓"}
      </span>
    </a>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? "/terminal";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password, needsTotp ? totpCode : undefined);
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message.replace(/^\d+:\s*/, "") : String(err);
      if (/totp|mfa|two[- ]?factor/i.test(msg)) {
        setNeedsTotp(true);
        setError("Enter the 6-digit code from your authenticator app.");
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h1 className="text-center text-lg font-semibold">
            Sign in to {branding?.name ?? "Piggy"}
          </h1>
        </CardHeader>
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {needsTotp && (
              <div>
                <Label htmlFor="totp">Authenticator code</Label>
                <Input
                  id="totp"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            )}
            <FormError>{error}</FormError>
            <Button type="submit" loading={submitting} className="w-full">
              Sign in
            </Button>
          </form>
          <div className="mt-4 flex justify-between text-xs text-slate-500">
            <Link to="/forgot-password" className="hover:text-brand">
              Forgot password?
            </Link>
            <Link to="/register" className="hover:text-brand">
              Create account
            </Link>
          </div>

          <div className="mt-3 text-center">
            <Link to="/features" className="text-xs text-slate-400 hover:text-slate-200">
              Platform overview →
            </Link>
          </div>

          {/* Phase 10C.7 — demo account self-serve. */}
          <div className="mt-6 border-t border-slate-200 dark:border-slate-800 pt-4">
            <p className="mb-2 text-center text-xs uppercase tracking-wider text-slate-400">
              Try with a demo account
            </p>
            <DemoButton />
            <p className="mt-2 text-center text-2xs text-slate-400">
              Seeded with $100,000 PAPER funds. Auto-expires in 30 days. No
              email verification needed.
            </p>
          </div>

          <div className="mt-6 border-t border-slate-200 dark:border-slate-800 pt-4">
            <p className="mb-2 text-center text-xs uppercase tracking-wider text-slate-400">
              Native desktop terminal
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              {DESKTOP_DOWNLOADS.map((d) => (
                <DownloadButton key={d.os} d={d} />
              ))}
            </div>
            <p className="mt-2 text-center text-2xs text-slate-400">
              Faster, charts off the GPU. Web terminal still works in any browser.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function DemoButton() {
  const [busy, setBusy] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  async function open() {
    setBusy(true); setErr(null);
    try {
      const resp = await fetch("/api/v1/demo/open", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        setErr(detail?.detail || `error ${resp.status}`);
        return;
      }
      const data = await resp.json();
      // Show seeded login info; the user can paste it into the form
      // (passwordless demo logins are wired in a follow-up; today the
      // user just sees that the demo account is active).
      alert(
        `Demo created.\nLogin: ${data.login_code}\nSeed: ${data.seed_balance} ${data.base_currency}.\n\n${data.note}`,
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button onClick={open} loading={busy} className="w-full">
        Open demo account
      </Button>
      {err && <p className="mt-2 text-center text-2xs text-red-500">{err}</p>}
    </>
  );
}
